import { Injectable } from '@angular/core';
import { filter, Observable, switchMap, take, Subject, map, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Credential, Revendedor, RevendedorForm } from '@core/models';
import { environment } from '@environments/environment';
import { ResponseObjectSuccess, ResponseSuccess } from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectCredential } from '@store/selectors/authentication.selectors';
import { selectResellerList } from '@store/selectors';
import { transformForUpdateReseller } from '@core/helpers/reseller/reseller-mapper';
import { MenuItem } from 'primeng/api';
import { transformDropdown } from '@core/helpers';

@Injectable({
  providedIn: 'root',
})
export class ResellerService {
  credential$: Observable<Credential | null>;
  resellerList$: Observable<Revendedor[] | null>;
  resellerList: Revendedor[] = [];

  private creationCompletedSource = new Subject<void>();
  creationCompleted$ = this.creationCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private closeFormSource = new Subject<void>();
  closeForm$ = this.closeFormSource.asObservable();

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>,
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.resellerList$ = this.store.select(selectResellerList);

    this.resellerList$.pipe(
      filter((resellerList) => !!resellerList),
      takeUntil(this.destroy$)
    ).subscribe((resellerList) => this.resellerList = resellerList);
  }

  creationCompleted(): void {
    this.creationCompletedSource.next();
    this.closeForm();
  }
  
  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeForm();
  }

  closeForm(): void {
    this.closeFormSource.next();
  }

  filter(text?: string | null): Observable<Revendedor[]> {
    return this.resellerList$.pipe(
      filter((resellerList): resellerList is Revendedor[] => resellerList !== null && resellerList.length > 0),
      map((resellerList) => !text ? resellerList : resellerList.filter((reseller) => reseller.nombre.toLowerCase().includes(text.toLowerCase()))),
      take(1),
    );
  }

  getNameById(id?: string): Observable<string|undefined> {
    return this.resellerList$.pipe(
      filter((resellerList): resellerList is Revendedor[] => resellerList !== null && resellerList.length > 0),
      map((resellerList) => resellerList.find(({ id: resellerId }) => id === resellerId)),
      map((reseller) => reseller?.nombre),
      take(1),
    );
  }

  getReseller(id: string): Observable<ResponseSuccess<Revendedor>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Revendedor>>(
          `${environment.microSeguridades}/api/v1/pos/resellers/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  removeReseller(id: string): Observable<ResponseSuccess<Revendedor>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.delete<ResponseSuccess<Revendedor>>(
          `${environment.microSeguridades}/api/v1/pos/resellers/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  getResellers(): Observable<ResponseSuccess<Revendedor>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';
              
        return this.http.get<ResponseSuccess<Revendedor>>(
          `${environment.microSeguridades}/api/v1/pos/resellers/`,
          {
            headers: { Authorization: token },
            params: {
              // tiendaId: credential?.idTienda as string,
            }
          },
        );
      }),
      take(1),
    );
  }

  getNameResellers(nombre:string): Observable<ResponseSuccess<Revendedor>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Revendedor>>(
          `${environment.microSeguridades}/api/v1/pos/resellers/`,
          {
            headers: { Authorization: token },
            params: {
              // tiendaId: credential?.idTienda as string,
              nombre_apellido: nombre,
            }
          },
        );
      }),
      take(1),
    );
  }

  create(resellerForm:RevendedorForm): Observable<ResponseObjectSuccess<Revendedor>> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';
        
        return this.http.post<ResponseObjectSuccess<Revendedor>>(
          `${environment.microSeguridades}/api/v1/pos/resellers`,
          transformForUpdateReseller({
            ...resellerForm,
          }),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  update(id:string,resellerForm: RevendedorForm): Observable<ResponseObjectSuccess<Revendedor>> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Revendedor>>(
          `${environment.microSeguridades}/api/v1/pos/resellers/${id}`,
          transformForUpdateReseller({
            ...resellerForm,
          }),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  getDropdown(): Observable<MenuItem[]> {
    return this.resellerList$.pipe(
      filter((stores): stores is Revendedor[] => !!stores),
      map((stores) => transformDropdown(stores, 'id', 'nombre',)),
    );
  }
}
